/**
 * Funções utilitárias para formatação de dados
 * 
 * IMPORTANTE: As funções LGPD foram movidas para o arquivo lgpd.js
 * Para usar as funções de criptografia e mascaramento LGPD, importe de './lgpd.js'
 * 
 * ============================================================================
 * FORMATADORES MONETÁRIOS PADRÃO DO SISTEMA
 * ============================================================================
 * 
 * Para campos monetários, use sempre estas funções:
 * 
 * 1. formatarMoedaInput(valor) - Para formatação em tempo real em inputs
 * 2. removerFormatacao(valor) - Para remover formatação e obter apenas números
 * 3. converterMoedaParaNumero(valor) - Para converter para número decimal
 * 4. formatarMoeda(valor) - Para exibição de valores já numéricos
 * 
 * Exemplo de uso em formulários:
 * - onChange: formatarMoedaInput(event.target.value)
 * - Validação: converterMoedaParaNumero(valor) > 0
 * - Envio para API: converterMoedaParaNumero(valorFormatado)
 */

// ============================================================================
// FUNÇÕES DE FORMATAÇÃO BÁSICA
// ============================================================================

/**
 * Formata um CPF (000.000.000-00)
 * @param {string} cpf - CPF a ser formatado
 * @returns {string} CPF formatado
 */
export const formatarCPF = (cpf) => {
  if (!cpf) return '';
  const numeros = cpf.replace(/\D/g, '');
  return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Formata um CNPJ (00.000.000/0000-00)
 * @param {string} cnpj - CNPJ a ser formatado
 * @returns {string} CNPJ formatado
 */
export const formatarCNPJ = (cnpj) => {
  if (!cnpj) return '';
  const numeros = cnpj.replace(/\D/g, '');
  return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

/**
 * Formata um CPF ou CNPJ automaticamente baseado no tamanho
 * @param {string} valor - CPF ou CNPJ a ser formatado
 * @returns {string} Valor formatado
 */
export const formatarCPFCNPJ = (valor) => {
  if (!valor) return '';
  const numeros = valor.replace(/\D/g, '');
  return numeros.length <= 11 ? formatarCPF(numeros) : formatarCNPJ(numeros);
};

/**
 * Formata um número de telefone ((00) 00000-0000)
 * @param {string} telefone - Telefone a ser formatado
 * @returns {string} Telefone formatado
 */
export const formatarTelefone = (telefone) => {
  if (!telefone) return '';
  const numeros = telefone.replace(/\D/g, '');
  if (numeros.length <= 10) {
    return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

/**
 * Formata uma data (DD/MM/AAAA) - Compatível com x-date-pickers
 * @param {string|Date} data - Data a ser formatada
 * @returns {string} Data formatada
 */
export const formatarData = (data) => {
  if (!data) return '';
  
  // Se for um objeto Date (compatível com x-date-pickers)
  if (data instanceof Date) {
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }
  
  // Se a data já estiver no formato YYYY-MM-DD (input type="date" ou API)
  if (typeof data === 'string' && data.includes('-')) {
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  }
  
  // Se for uma string no formato DD/MM/YYYY (já formatada)
  if (typeof data === 'string' && data.includes('/')) {
    return data;
  }
  
  // Se for uma string de números
  if (typeof data === 'string') {
  const numeros = data.replace(/\D/g, '');
  if (numeros.length === 8) {
    return numeros.replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3');
  }
  }
  
  return data;
};

/**
 * Formata um valor monetário (R$ 0,00)
 * @param {number|string} valor - Valor a ser formatado
 * @param {number} casasDecimais - Número de casas decimais (padrão: 2)
 * @returns {string} Valor formatado
 */
export const formatarMoeda = (valor, casasDecimais = 2) => {
  if (!valor && valor !== 0) return '';
  const numero = typeof valor === 'string' ? parseFloat(valor.replace(/\D/g, '')) / 100 : valor;
  return numero.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: casasDecimais,
    maximumFractionDigits: casasDecimais
  });
};

/**
 * FORMATADOR PADRÃO PARA VALORES MONETÁRIOS EM INPUTS
 * 
 * Esta função é a formatação padrão para todos os campos monetários do sistema.
 * Formata valores em tempo real enquanto o usuário digita, convertendo para o padrão brasileiro.
 * 
 * Características:
 * - Aceita apenas números (remove automaticamente caracteres especiais)
 * - Converte automaticamente para centavos (ex: digitar "1000" = R$ 10,00)
 * - Formata no padrão brasileiro (R$ 0,00)
 * - Retorna string vazia se não houver valor
 * 
 * @param {string} valor - Valor digitado pelo usuário (pode conter caracteres especiais)
 * @returns {string} Valor formatado no padrão "R$ 0,00" ou string vazia
 * 
 * @example
 * formatarMoedaInput("1000")     // "R$ 10,00"
 * formatarMoedaInput("123456")   // "R$ 1.234,56"
 * formatarMoedaInput("abc")      // ""
 * formatarMoedaInput("")         // ""
 */
export const formatarMoedaInput = (valor) => {
  // Se não há valor, retorna string vazia
  if (!valor || typeof valor !== 'string') return '';
  
  // Remove todos os caracteres não numéricos (mantém apenas 0-9)
  const numeros = valor.replace(/\D/g, '');
  
  // Se não há números após a limpeza, retorna string vazia
  if (numeros.length === 0) return '';
  
  // Converte para centavos e depois para reais
  // Ex: "1000" -> 1000 centavos -> 10,00 reais
  const valorEmCentavos = parseInt(numeros, 10);
  const valorEmReais = valorEmCentavos / 100;
  
  // Formata como moeda brasileira usando toLocaleString
  // Garante sempre 2 casas decimais e separadores corretos
  return valorEmReais.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * REMOVEDOR PADRÃO DE FORMATAÇÃO MONETÁRIA
 * 
 * Esta função remove toda a formatação de valores monetários para conversão numérica.
 * É usada em conjunto com formatarMoedaInput para converter valores formatados de volta para números.
 * 
 * Características:
 * - Remove todos os caracteres não numéricos (R$, espaços, pontos, vírgulas, etc.)
 * - Retorna apenas os números em formato string
 * - Ideal para conversão de valores formatados para cálculos
 * 
 * @param {string} valor - Valor formatado (ex: "R$ 1.234,56")
 * @returns {string} Valor sem formatação (ex: "123456")
 * 
 * @example
 * removerFormatacao("R$ 1.234,56")  // "123456"
 * removerFormatacao("R$ 10,00")     // "1000"
 * removerFormatacao("abc123def")    // "123"
 * removerFormatacao("")             // ""
 */
export const removerFormatacao = (valor) => {
  // Se não há valor, retorna string vazia
  if (!valor || typeof valor !== 'string') return '';
  
  // Remove todos os caracteres não numéricos (mantém apenas 0-9)
  // Isso remove R$, espaços, pontos, vírgulas, etc.
  return valor.replace(/\D/g, '');
};

/**
 * CONVERSOR PADRÃO DE VALORES MONETÁRIOS FORMATADOS PARA NÚMEROS
 * 
 * Esta função converte valores monetários formatados de volta para números decimais.
 * É usada para validações e envio de dados para API após formatação em inputs.
 * 
 * Características:
 * - Remove toda a formatação monetária
 * - Converte de centavos para reais automaticamente
 * - Retorna número decimal pronto para cálculos
 * - Trata valores inválidos retornando 0
 * 
 * @param {string} valorFormatado - Valor formatado (ex: "R$ 1.234,56")
 * @returns {number} Valor numérico em reais (ex: 1234.56)
 * 
 * @example
 * converterMoedaParaNumero("R$ 1.234,56")  // 1234.56
 * converterMoedaParaNumero("R$ 10,00")     // 10.00
 * converterMoedaParaNumero("R$ 0,50")      // 0.50
 * converterMoedaParaNumero("abc")          // 0
 * converterMoedaParaNumero("")             // 0
 */
export const converterMoedaParaNumero = (valorFormatado) => {
  // Se não há valor, retorna 0
  if (!valorFormatado || typeof valorFormatado !== 'string') return 0;
  
  // Remove toda a formatação e obtém apenas os números
  const numeros = removerFormatacao(valorFormatado);
  
  // Se não há números, retorna 0
  if (numeros.length === 0) return 0;
  
  // Converte para centavos e depois para reais
  // Ex: "123456" -> 123456 centavos -> 1234.56 reais
  const valorEmCentavos = parseInt(numeros, 10);
  const valorEmReais = valorEmCentavos / 100;
  
  // Retorna o valor numérico com precisão de 2 casas decimais
  return Math.round(valorEmReais * 100) / 100;
};

/**
 * Formata um CEP (00000-000)
 * @param {string} cep - CEP a ser formatado
 * @returns {string} CEP formatado
 */
export const formatarCEP = (cep) => {
  if (!cep) return '';
  const numeros = cep.replace(/\D/g, '');
  return numeros.replace(/(\d{5})(\d{3})/, '$1-$2');
};

/**
 * Formata um número de cartão de crédito (0000 0000 0000 0000)
 * @param {string} numero - Número do cartão a ser formatado
 * @returns {string} Número formatado
 */
export const formatarCartaoCredito = (numero) => {
  if (!numero) return '';
  const numeros = numero.replace(/\D/g, '');
  return numeros.replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
};

/**
 * Formata um número de PIS/PASEP (000.00000.00-0)
 * @param {string} pis - Número do PIS/PASEP a ser formatado
 * @returns {string} Número formatado
 */
export const formatarPIS = (pis) => {
  if (!pis) return '';
  const numeros = pis.replace(/\D/g, '');
  return numeros.replace(/(\d{3})(\d{5})(\d{2})(\d{1})/, '$1.$2.$3-$4');
};

/**
 * Formata um número de RG (00.000.000-0)
 * @param {string} rg - Número do RG a ser formatado
 * @returns {string} Número formatado
 */
export const formatarRG = (rg) => {
  if (!rg) return '';
  const numeros = rg.replace(/\D/g, '');
  return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4');
};

/**
 * Remove a formatação (máscaras) de campos comuns em um objeto de dados.
 * Útil para limpar dados antes de enviar para a API.
 * @param {Object} dados - O objeto de dados do formulário.
 * @param {Array<string>} camposAdicionais - Uma lista de nomes de campos adicionais para limpar.
 * @returns {Object} Um novo objeto com os campos limpos.
 */
export const limparMascarasDeCamposComuns = (dados, camposAdicionais = []) => {
  const camposParaLimpar = [
    'cpf', 
    'cnpj', 
    'cpfCnpj', 
    'telefone', 
    'celular', 
    'cep',
    ...camposAdicionais
  ];
  
  const dadosLimpos = { ...dados };

  camposParaLimpar.forEach(campo => {
    if (typeof dadosLimpos[campo] === 'string') {
      dadosLimpos[campo] = dadosLimpos[campo].replace(/\D/g, '');
    }
  });

  return dadosLimpos;
};

/**
 * Valida um CPF
 * @param {string} cpf - CPF a ser validado
 * @returns {boolean} True se o CPF for válido
 */
export const validarCPF = (cpf) => {
  if (!cpf) return false;
  
  // Remove caracteres não numéricos
  const numeros = cpf.replace(/\D/g, '');
  
  // Verifica se tem 11 dígitos
  if (numeros.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(numeros)) return false;
  
  // Calcula o primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(numeros[i]) * (10 - i);
  }
  let resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;
  
  // Calcula o segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(numeros[i]) * (11 - i);
  }
  resto = soma % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;
  
  // Verifica se os dígitos calculados são iguais aos do CPF
  return parseInt(numeros[9]) === digito1 && parseInt(numeros[10]) === digito2;
};

/**
 * Valida um CNPJ
 * @param {string} cnpj - CNPJ a ser validado
 * @returns {boolean} True se o CNPJ for válido
 */
export const validarCNPJ = (cnpj) => {
  if (!cnpj) return false;
  
  // Remove caracteres não numéricos
  const numeros = cnpj.replace(/\D/g, '');
  
  // Verifica se tem 14 dígitos
  if (numeros.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{13}$/.test(numeros)) return false;
  
  // Calcula o primeiro dígito verificador
  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let soma = 0;
  for (let i = 0; i < 12; i++) {
    soma += parseInt(numeros[i]) * pesos1[i];
  }
  let resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;
  
  // Calcula o segundo dígito verificador
  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  soma = 0;
  for (let i = 0; i < 13; i++) {
    soma += parseInt(numeros[i]) * pesos2[i];
  }
  resto = soma % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;
  
  // Verifica se os dígitos calculados são iguais aos do CNPJ
  return parseInt(numeros[12]) === digito1 && parseInt(numeros[13]) === digito2;
};

/**
 * Formata uma hora (HH:MM)
 * @param {string} hora - Hora a ser formatada
 * @returns {string} Hora formatada
 */
export const formatarHora = (hora) => {
  if (!hora) return '';
  
  // Se for uma string de números
  const numeros = hora.replace(/\D/g, '');
  if (numeros.length === 4) {
    return numeros.replace(/(\d{2})(\d{2})/, '$1:$2');
  }
  
  // Se for um objeto Date
  if (hora instanceof Date) {
    const horas = String(hora.getHours()).padStart(2, '0');
    const minutos = String(hora.getMinutes()).padStart(2, '0');
    return `${horas}:${minutos}`;
  }
  
  return hora;
};

/**
 * Formata uma data e hora (DD/MM/AAAA HH:MM) - Compatível com x-date-pickers
 * @param {string|Date} dataHora - Data e hora a ser formatada
 * @returns {string} Data e hora formatada
 */
export const formatarDataHora = (dataHora) => {
  if (!dataHora) return '';
  
  // Se for um objeto Date (compatível com x-date-pickers)
  if (dataHora instanceof Date) {
    if (isNaN(dataHora.getTime())) return '';
    const dia = String(dataHora.getDate()).padStart(2, '0');
    const mes = String(dataHora.getMonth() + 1).padStart(2, '0');
    const ano = dataHora.getFullYear();
    const horas = String(dataHora.getHours()).padStart(2, '0');
    const minutos = String(dataHora.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }
  
  // Se for uma string no formato ISO
  if (typeof dataHora === 'string' && dataHora.includes('T')) {
    const data = new Date(dataHora);
    if (!isNaN(data.getTime())) {
      return formatarDataHora(data);
    }
  }
  
  // Se for uma string no formato YYYY-MM-DD HH:MM
  if (typeof dataHora === 'string' && dataHora.includes('-') && dataHora.includes(' ')) {
    const [dataPart, horaPart] = dataHora.split(' ');
    const [ano, mes, dia] = dataPart.split('-');
    return `${dia}/${mes}/${ano} ${horaPart}`;
  }
  
  return dataHora;
};

// ============================================================================
// FUNÇÕES DE MENSAGENS DO SISTEMA (PADRÃO)
// ============================================================================

// Tipos de mensagem disponíveis
export const MESSAGE_TYPES = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success',
  NOTIFICATION: 'notification'
};

/**
 * Cria mensagem de erro de API padronizada
 * @param {Object} error - Objeto de erro da API
 * @param {string} operation - Nome da operação que falhou
 * @returns {string} Mensagem de erro formatada
 */
export const createApiErrorMessage = (error, operation = 'Operação') => {
  if (error.response) {
    const status = error.response.status;
    const message = error.response.data?.message || error.response.data?.error || 'Erro desconhecido';
    
    switch (status) {
      case 400:
        return `${operation} inválida: ${message}`;
      case 401:
        return 'Sessão expirada. Faça login novamente.';
      case 403:
        return 'Acesso negado. Você não tem permissão para esta operação.';
      case 404:
        return `${operation} não encontrada.`;
      case 409:
        return `${operation} conflitante: ${message}`;
      case 422:
        return `Dados inválidos: ${message}`;
      case 500:
        return 'Erro interno do servidor. Tente novamente mais tarde.';
      default:
        return `Erro ${status}: ${message}`;
    }
  } else if (error.request) {
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  } else {
    return `Erro: ${error.message}`;
  }
};

/**
 * Cria mensagem de validação padronizada
 * @param {string} field - Nome do campo
 * @param {string} rule - Regra de validação
 * @returns {string} Mensagem de validação formatada
 */
export const createValidationMessage = (field, rule) => {
  const fieldNames = {
    nome: 'Nome',
    email: 'E-mail',
    senha: 'Senha',
    telefone: 'Telefone',
    cpf: 'CPF',
    cnpj: 'CNPJ',
    data: 'Data',
    hora: 'Hora',
    valor: 'Valor',
    quantidade: 'Quantidade',
    descricao: 'Descrição',
    observacao: 'Observação'
  };

  const fieldName = fieldNames[field] || field;
  
  const ruleMessages = {
    required: `${fieldName} é obrigatório.`,
    min: `${fieldName} deve ter pelo menos {min} caracteres.`,
    max: `${fieldName} deve ter no máximo {max} caracteres.`,
    email: `${fieldName} deve ser um e-mail válido.`,
    cpf: `${fieldName} deve ser um CPF válido.`,
    cnpj: `${fieldName} deve ser um CNPJ válido.`,
    telefone: `${fieldName} deve ser um telefone válido.`,
    data: `${fieldName} deve ser uma data válida.`,
    hora: `${fieldName} deve ser uma hora válida.`,
    valor: `${fieldName} deve ser um valor válido.`,
    positivo: `${fieldName} deve ser um valor positivo.`,
    futuro: `${fieldName} deve ser uma data futura.`,
    passado: `${fieldName} deve ser uma data passada.`,
    unico: `${fieldName} já existe no sistema.`,
    formato: `${fieldName} está em formato inválido.`
  };

  return ruleMessages[rule] || `${fieldName} é inválido.`;
};

/**
 * Cria mensagem de operação CRUD padronizada
 * @param {string} operation - Tipo de operação (create, read, update, delete, save)
 * @param {string} entity - Nome da entidade
 * @param {boolean} success - Se a operação foi bem-sucedida
 * @returns {string} Mensagem CRUD formatada
 */
export const createCrudMessage = (operation, entity, success = true) => {
  const operations = {
    create: {
      success: `${entity} criado com sucesso!`,
      error: `Erro ao criar ${entity}.`
    },
    read: {
      success: `${entity} carregado com sucesso!`,
      error: `Erro ao carregar ${entity}.`
    },
    update: {
      success: `${entity} atualizado com sucesso!`,
      error: `Erro ao atualizar ${entity}.`
    },
    delete: {
      success: `${entity} removido com sucesso!`,
      error: `Erro ao remover ${entity}.`
    },
    save: {
      success: `${entity} salvo com sucesso!`,
      error: `Erro ao salvar ${entity}.`
    }
  };

  const op = operations[operation] || operations.save;
  return success ? op.success : op.error;
};

/**
 * Cria mensagem de status do sistema padronizada
 * @param {string} status - Status do sistema
 * @param {string} details - Detalhes adicionais
 * @returns {string} Mensagem de status formatada
 */
export const createSystemMessage = (status, details = '') => {
  const statusMessages = {
    loading: 'Carregando...',
    saving: 'Salvando...',
    processing: 'Processando...',
    connecting: 'Conectando...',
    disconnected: 'Conexão perdida. Tentando reconectar...',
    reconnected: 'Conexão restaurada!',
    maintenance: 'Sistema em manutenção. Tente novamente em alguns minutos.',
    offline: 'Sistema offline. Verifique sua conexão.',
    timeout: 'Tempo limite excedido. Tente novamente.',
    busy: 'Sistema ocupado. Tente novamente em alguns segundos.'
  };

  const message = statusMessages[status] || 'Status desconhecido';
  return details ? `${message} ${details}` : message;
};

/**
 * Cria mensagem de notificação padronizada
 * @param {string} type - Tipo de notificação
 * @param {string} content - Conteúdo personalizado
 * @returns {string} Mensagem de notificação formatada
 */
export const createNotificationMessage = (type, content) => {
  const notifications = {
    new_user: 'Novo usuário registrado no sistema.',
    new_order: 'Novo pedido recebido.',
    payment_received: 'Pagamento recebido com sucesso.',
    reminder: 'Lembrete: você tem tarefas pendentes.',
    update_available: 'Nova atualização disponível.',
    backup_complete: 'Backup concluído com sucesso.',
    sync_complete: 'Sincronização concluída.',
    import_complete: 'Importação concluída com sucesso.',
    export_complete: 'Exportação concluída com sucesso.',
    report_ready: 'Relatório está pronto para download.'
  };

  return notifications[type] || content || 'Nova notificação.';
};

/**
 * Determina o tipo de mensagem baseado no contexto
 * @param {string} context - Contexto da mensagem
 * @param {string} severity - Severidade (info, warning, error, success)
 * @returns {string} Tipo de mensagem
 */
export const getMessageType = (context, severity = 'info') => {
  const contextTypes = {
    error: MESSAGE_TYPES.ERROR,
    warning: MESSAGE_TYPES.WARNING,
    success: MESSAGE_TYPES.SUCCESS,
    info: MESSAGE_TYPES.INFO,
    notification: MESSAGE_TYPES.NOTIFICATION
  };

  // Se o contexto já é um tipo válido, use-o
  if (Object.values(MESSAGE_TYPES).includes(context)) {
    return context;
  }

  // Mapeamento de contextos para tipos
  const contextMapping = {
    api: severity === 'error' ? MESSAGE_TYPES.ERROR : MESSAGE_TYPES.INFO,
    validation: MESSAGE_TYPES.WARNING,
    crud: severity === 'error' ? MESSAGE_TYPES.ERROR : MESSAGE_TYPES.SUCCESS,
    system: severity === 'error' ? MESSAGE_TYPES.ERROR : MESSAGE_TYPES.INFO,
    notification: MESSAGE_TYPES.NOTIFICATION,
    security: MESSAGE_TYPES.WARNING,
    performance: MESSAGE_TYPES.INFO
  };

  return contextMapping[context] || contextTypes[severity] || MESSAGE_TYPES.INFO;
};

/**
 * Formata mensagens longas com reticências
 * @param {string} message - Mensagem original
 * @param {number} maxLength - Comprimento máximo (padrão: 100)
 * @returns {string} Mensagem formatada
 */
export const formatLongMessage = (message, maxLength = 100) => {
  if (message.length <= maxLength) {
    return message;
  }
  
  return message.substring(0, maxLength - 3) + '...';
};

/**
 * Cria mensagem com emojis (opcional)
 * @param {string} type - Tipo de mensagem
 * @param {string} message - Mensagem original
 * @returns {string} Mensagem com emoji
 */
export const createEmojiMessage = (type, message) => {
  const emojis = {
    [MESSAGE_TYPES.SUCCESS]: '✅',
    [MESSAGE_TYPES.ERROR]: '❌',
    [MESSAGE_TYPES.WARNING]: '⚠️',
    [MESSAGE_TYPES.INFO]: 'ℹ️',
    [MESSAGE_TYPES.NOTIFICATION]: '🔔'
  };

  const emoji = emojis[type] || '';
  return emoji ? `${emoji} ${message}` : message;
};

// ============================================================================
// FUNÇÕES PARA DATEPICKERS - EVITA PROBLEMAS DE TIMEZONE
// ============================================================================

/**
 * Formata data do DatePicker para DD/MM/AAAA (evita problemas de timezone)
 * 
 * IMPORTANTE: Use esta função sempre que trabalhar com DatePickers do MUI
 * para evitar problemas de conversão de timezone que causam diferença de 1 dia.
 * 
 * @param {Date} data - Objeto Date do DatePicker
 * @returns {string} Data formatada no padrão DD/MM/AAAA
 * 
 * @example
 * // No onChange do DatePicker:
 * onChange={(data) => {
 *   if (data) {
 *     const dataFormatada = formatarDataDatePicker(data);
 *     setFormData(prev => ({ ...prev, dataInicio: dataFormatada }));
 *   }
 * }}
 */
export const formatarDataDatePicker = (data) => {
  if (!data || !(data instanceof Date)) {
    return '';
  }
  
  // Usar métodos nativos para evitar problemas de timezone
  const dia = String(data.getDate()).padStart(2, '0');
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const ano = data.getFullYear();
  
  return `${dia}/${mes}/${ano}`;
};

/**
 * Converte string DD/MM/AAAA para objeto Date (para usar no value do DatePicker)
 * 
 * @param {string} dataFormatada - Data no formato DD/MM/AAAA
 * @returns {Date|null} Objeto Date ou null se inválida
 * 
 * @example
 * // No value do DatePicker:
 * value={formData.dataInicio ? converterDataParaDatePicker(formData.dataInicio) : null}
 */
export const converterDataParaDatePicker = (dataFormatada) => {
  if (!dataFormatada || typeof dataFormatada !== 'string') {
    return null;
  }
  
  try {
    // Verificar se está no formato DD/MM/AAAA
    const match = dataFormatada.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
      return null;
    }
    
    const [, dia, mes, ano] = match;
    
    // Criar data local (evita problemas de timezone)
    const data = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
    
    // Verificar se a data é válida
    if (isNaN(data.getTime())) {
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Erro ao converter data para DatePicker:', error);
    return null;
  }
};

/**
 * Valida se uma data é válida no formato DD/MM/AAAA
 * 
 * @param {string} dataFormatada - Data no formato DD/MM/AAAA
 * @returns {boolean} true se a data é válida
 */
export const validarDataFormatada = (dataFormatada) => {
  if (!dataFormatada || typeof dataFormatada !== 'string') {
    return false;
  }
  
  const match = dataFormatada.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) {
    return false;
  }
  
  const [, dia, mes, ano] = match;
  const data = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
  
  return !isNaN(data.getTime()) && 
         data.getDate() === parseInt(dia) && 
         data.getMonth() === parseInt(mes) - 1 && 
         data.getFullYear() === parseInt(ano);
}; 

// ============================================================================
// FUNÇÕES PARA X-DATE-PICKERS (MATERIAL-UI)
// ============================================================================

/**
 * Converte string YYYY-MM-DD para objeto Date (para usar no value do DatePicker)
 * @param {string} dataString - Data no formato YYYY-MM-DD
 * @returns {Date|null} Objeto Date ou null se inválido
 */
export const converterStringParaDate = (dataString) => {
  if (!dataString) return null;
  
  // Se já for um objeto Date, retorna ele mesmo
  if (dataString instanceof Date) {
    return dataString;
  }
  
  // Se for string no formato YYYY-MM-DD
  if (typeof dataString === 'string' && dataString.includes('-')) {
    const data = new Date(dataString + 'T00:00:00');
    return isNaN(data.getTime()) ? null : data;
  }
  
  // Se for string no formato DD/MM/YYYY
  if (typeof dataString === 'string' && dataString.includes('/')) {
    const [dia, mes, ano] = dataString.split('/');
    const data = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
    return isNaN(data.getTime()) ? null : data;
  }
  
  return null;
};

/**
 * Converte objeto Date para string YYYY-MM-DD (para enviar para API)
 * @param {Date} data - Objeto Date
 * @returns {string} Data no formato YYYY-MM-DD ou string vazia
 */
export const converterDateParaString = (data) => {
  if (!data || !(data instanceof Date) || isNaN(data.getTime())) {
    return '';
  }
  
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  
  return `${ano}-${mes}-${dia}`;
};

/**
 * Formata data para exibição no DatePicker (DD/MM/YYYY)
 * @param {Date|string} data - Data a ser formatada
 * @returns {string} Data formatada para exibição
 */
export const formatarDataParaExibicao = (data) => {
  if (!data) return '';
  
  let dataObj;
  
  if (data instanceof Date) {
    dataObj = data;
  } else if (typeof data === 'string') {
    dataObj = converterStringParaDate(data);
  } else {
    return '';
  }
  
  if (!dataObj || isNaN(dataObj.getTime())) {
    return '';
  }
  
  const dia = String(dataObj.getDate()).padStart(2, '0');
  const mes = String(dataObj.getMonth() + 1).padStart(2, '0');
  const ano = dataObj.getFullYear();
  
  return `${dia}/${mes}/${ano}`;
};

/**
 * Valida se uma data é válida para o DatePicker
 * @param {Date|string} data - Data a ser validada
 * @returns {boolean} True se a data for válida
 */
export const validarDataParaDatePicker = (data) => {
  if (!data) return false;
  
  let dataObj;
  
  if (data instanceof Date) {
    dataObj = data;
  } else if (typeof data === 'string') {
    dataObj = converterStringParaDate(data);
  } else {
    return false;
  }
  
  return dataObj && !isNaN(dataObj.getTime());
};

/**
 * Configuração padrão para DatePicker do Material-UI
 * @returns {object} Configuração padrão do DatePicker
 */
export const getConfiguracaoDatePicker = () => {
  return {
    format: 'dd/MM/yyyy',
    slotProps: {
      textField: {
        fullWidth: true,
        size: 'medium',
        sx: { '& .MuiInputBase-root': { fontSize: '1rem' } }
      }
    }
  };
};

/**
 * Configuração de LocalizationProvider para português brasileiro
 * @returns {object} Configuração do LocalizationProvider
 */
export const getConfiguracaoLocalizationProvider = () => {
  return {
    dateAdapter: 'AdapterDateFns',
    adapterLocale: 'ptBR'
  };
};

// ============================================================================
// EXEMPLOS DE USO COM X-DATE-PICKERS
// ============================================================================

/**
 * EXEMPLO DE USO COMPLETO COM X-DATE-PICKERS:
 * 
 * 1. IMPORTS NECESSÁRIOS:
 * ```javascript
 * import { DatePicker } from '@mui/x-date-pickers/DatePicker';
 * import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
 * import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
 * import { ptBR } from 'date-fns/locale';
 * import { 
 *   converterStringParaDate, 
 *   converterDateParaString, 
 *   formatarDataParaExibicao,
 *   validarDataParaDatePicker 
 * } from '../utils/formatadores';
 * ```
 * 
 * 2. COMPONENTE COM DATEPICKER:
 * ```javascript
 * const [dataInicio, setDataInicio] = useState('');
 * 
 * <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
 *   <DatePicker
 *     label="Data de Início"
 *     value={converterStringParaDate(dataInicio)}
 *     onChange={(newValue) => {
 *       setDataInicio(converterDateParaString(newValue));
 *     }}
 *     format="dd/MM/yyyy"
 *     slotProps={{
 *       textField: {
 *         fullWidth: true,
 *         size: 'medium',
 *         helperText: dataInicio ? formatarDataParaExibicao(dataInicio) : 'Selecione uma data',
 *         sx: { '& .MuiInputBase-root': { fontSize: '1rem' } }
 *       }
 *     }}
 *   />
 * </LocalizationProvider>
 * ```
 * 
 * 3. VALIDAÇÃO:
 * ```javascript
 * const isDataValida = validarDataParaDatePicker(dataInicio);
 * ```
 * 
 * 4. ENVIO PARA API:
 * ```javascript
 * const dadosParaAPI = {
 *   dataInicio: converterDateParaString(dataInicio), // YYYY-MM-DD
 *   dataFim: converterDateParaString(dataFim)
 * };
 * ```
 */ 